import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, Search } from "lucide-react";
import { api, errorText, fmtDate } from "@/admin/api";
import { PageHeader, Badge, Empty, Spinner, Button, inputCls } from "@/admin/ui";
import { toCsv, download } from "@/admin/pages/Enquiries";

export default function Waitlist() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/waitlist").then((r) => setItems(r.data.items)); }, []);

  const remove = async (w) => {
    if (!window.confirm(`Remove ${w.email} from the waiting list?`)) return;
    try {
      await api.delete(`/waitlist/${w.id}`);
      setItems((l) => l.filter((x) => x.id !== w.id));
      toast.success("Removed from waitlist");
    } catch (err) { toast.error(errorText(err)); }
  };

  if (!items) return <Spinner />;
  const shown = items.filter((w) => `${w.name} ${w.email} ${w.interest}`.toLowerCase().includes(q.toLowerCase()));
  const byInterest = items.reduce((m, w) => ({ ...m, [w.interest]: (m[w.interest] || 0) + 1 }), {});

  return (
    <div data-testid="admin-waitlist">
      <PageHeader
        eyebrow="Desantrix Academy"
        title="Waiting list"
        sub={`${items.length} future students · welcome emails go out automatically from welcome@desantrix.com`}
        actions={<Button variant="ghost" onClick={() => download("desantrix-academy-waitlist.csv", toCsv(items, ["created_at", "name", "email", "interest", "welcomed"]))} data-testid="waitlist-export"><Download className="h-4 w-4" /> Export CSV</Button>}
      />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="waitlist-interest-summary">
        {Object.entries(byInterest).map(([k, v]) => <span key={k} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600"><span className="font-bold text-[#0B1528]">{v}</span> · {k}</span>)}
      </div>
      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className={`${inputCls} pl-10`} data-testid="waitlist-search" />
      </div>
      {shown.length === 0 ? <Empty>No one on the list yet.</Empty> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="waitlist-table">
            <thead className="bg-slate-50 text-left font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Interest</th><th className="px-5 py-3">Joined</th><th className="px-5 py-3">Welcome</th><th className="px-5 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((w) => (
                <tr key={w.id} className="hover:bg-slate-50/60" data-testid={`waitlist-row-${w.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0B1528]">{w.name}</td>
                  <td className="px-5 py-3 text-slate-600"><a href={`mailto:${w.email}`} className="hover:text-amber-600">{w.email}</a></td>
                  <td className="px-5 py-3 text-slate-600">{w.interest}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(w.created_at)}</td>
                  <td className="px-5 py-3"><Badge tone={w.welcomed ? "green" : "slate"}>{w.welcomed ? "sent" : "not sent"}</Badge></td>
                  <td className="px-5 py-3 text-right"><button onClick={() => remove(w)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Remove" data-testid={`waitlist-delete-${w.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
